import type { AuthAudience, StoredTokens } from './types';

// Xapien pattern: localStorage + BroadcastChannel so a login or logout in
// one tab immediately reflects in the others. Channel name is app-scoped so
// it doesn't conflict with anything else on the same origin.

const KEYS = {
  accessToken: 'waf.auth.access_token',
  idToken: 'waf.auth.id_token',
  refreshToken: 'waf.auth.refresh_token',
  tokenType: 'waf.auth.token_type',
  tokenExpiry: 'waf.auth.token_expiry',
  audience: 'waf.auth.audience'
} as const;

const CHANNEL_NAME = 'waf.auth';
const UPDATED_EVENT = 'waf.auth.tokens_updated';

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

export function storeTokens(tokens: Omit<StoredTokens, 'tokenExpiry'> & { expiresIn: number }): void {
  const expiry = Date.now() + tokens.expiresIn * 1000;
  localStorage.setItem(KEYS.accessToken, tokens.accessToken);
  localStorage.setItem(KEYS.idToken, tokens.idToken);
  localStorage.setItem(KEYS.refreshToken, tokens.refreshToken);
  localStorage.setItem(KEYS.tokenType, tokens.tokenType);
  localStorage.setItem(KEYS.tokenExpiry, expiry.toString());
  localStorage.setItem(KEYS.audience, tokens.audience);
  getChannel()?.postMessage({ type: 'tokens_updated' });
  window.dispatchEvent(new CustomEvent(UPDATED_EVENT));
}

export function getStoredTokens(): StoredTokens | null {
  const accessToken = localStorage.getItem(KEYS.accessToken);
  const idToken = localStorage.getItem(KEYS.idToken);
  const refreshToken = localStorage.getItem(KEYS.refreshToken);
  const tokenType = localStorage.getItem(KEYS.tokenType);
  const expiry = localStorage.getItem(KEYS.tokenExpiry);
  const audienceRaw = localStorage.getItem(KEYS.audience);
  if (!accessToken || !idToken || !refreshToken || !expiry) return null;
  const audience: AuthAudience = audienceRaw === 'entrant' ? 'entrant' : 'admin';
  return {
    accessToken,
    idToken,
    refreshToken,
    tokenType: tokenType || 'Bearer',
    tokenExpiry: Number.parseInt(expiry, 10),
    audience
  };
}

export function clearTokens(): void {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
  getChannel()?.postMessage({ type: 'tokens_cleared' });
  window.dispatchEvent(new CustomEvent(UPDATED_EVENT));
}

export function isTokenExpired(skewSeconds = 30): boolean {
  const tokens = getStoredTokens();
  if (!tokens) return true;
  return Date.now() + skewSeconds * 1000 >= tokens.tokenExpiry;
}

export function subscribeToTokenChanges(callback: () => void): () => void {
  const ch = getChannel();
  const handler = () => callback();
  const msgHandler = () => callback();
  window.addEventListener(UPDATED_EVENT, handler);
  ch?.addEventListener('message', msgHandler);
  return () => {
    window.removeEventListener(UPDATED_EVENT, handler);
    ch?.removeEventListener('message', msgHandler);
  };
}
